import React from 'react';
import { Icon } from '@iconify/react';
import { formatIntDateTime } from '../utils/dateUtils.js';

const FormHistoryDataTable = ({
  forms = [],
  onView,
  loading = false,
  variant = 'completed',
  onDelete,
  canDelete = false,
  planthireEnabled,
  sortField,
  sortOrder,
  onSort,
}) => {
  const isCompletedView = variant === 'completed';
  const isAssignedView = variant === 'assigned';
  const showActions = !isCompletedView && canDelete;
  const columnCount = isAssignedView
    ? (planthireEnabled ? 11 : 9)
    : isCompletedView
      ? 9
      : showActions
        ? 9
        : 8;

  const getSortIcon = (field) => {
    if (sortField !== field || !sortOrder) {
      return 'mdi:sort';
    }
    return sortOrder === 'asc' ? 'mdi:sort-ascending' : 'mdi:sort-descending';
  };

  const renderSortableHeader = (label, field) => {
    const ariaSort = sortField === field
      ? sortOrder === 'asc'
        ? 'ascending'
        : sortOrder === 'desc'
          ? 'descending'
          : 'none'
      : 'none';

    return (
      <th
        onClick={() => onSort?.(field)}
        style={{ cursor: 'pointer' }}
        scope="col"
        aria-sort={ariaSort}
      >
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <Icon className="text-xl" icon={getSortIcon(field)} />
        </span>
      </th>
    );
  };

  return (
    <table className="table bordered-table mb-3 w-100 rounded-0">
      <thead>
        <tr>
          <th className="rounded-0"></th>
          <th>Form #</th>
          {renderSortableHeader('Form Name', 'formName')}
          {renderSortableHeader('Asset/Area', 'assetArea')}
          {planthireEnabled && (
            <>
              <th>Customer</th>
              <th>Project</th>
            </>
          )}
          {renderSortableHeader('Site / Department', 'department')}
          <th>Work Order</th>
          {renderSortableHeader('Form Type', 'formType')}
          {isAssignedView ? (
            <>
              <th>Assigned To</th>
              <th>Assigned At</th>
            </>
          ) : (
            <>
              {renderSortableHeader('Entered By', 'enteredBy')}
              <th>{isCompletedView ? 'Completed At' : 'Started At'}</th>
              <th>Completed Items</th>
              {isCompletedView ? (
                <th>Form Link</th>
              ) : (
                showActions && <th className="text-center">Actions</th>
              )}
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {forms.length === 0 && !loading && (
          <tr>
            <td colSpan={columnCount} className="text-center py-4 rounded-0">
              No Forms Loaded
            </td>
          </tr>
        )}
        {forms.map((row) => {
          console.log('row', row);
          const imageUrl = row.form?.image
            ? `${process.env.REACT_APP_SPACES_BASE}/${row.form.image}`
            : `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
          const enteredBy = row.completedByName ? row.completedByName : row.updatedByUser?.name || '';
          const timestamp = isAssignedView
            ? row.assignedAt
            : isCompletedView
              ? row.updatedAt
              : row.createdAt;
          const formattedTimestamp = timestamp ? formatIntDateTime(timestamp) : '';

          return (
            <tr key={row.id}>
              <td>
                <img
                  src={imageUrl}
                  alt="form"
                  style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '0',
                    objectFit: 'cover',
                    border: '1px solid #ddd',
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `${process.env.REACT_APP_SPACES_BASE}/placeholder.png`;
                  }}
                />
              </td>
              <td>{row.id ? `FO-${String(row.id).padStart(6, '0')}` : ''}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-link p-0"
                  onClick={() => onView && onView(row)}
                >
                  {row.formName || row.form?.name || ''}
                </button>
              </td>
              <td>
                {row.asset
                  ? `${row.asset.assetNumber || ''} ${row.asset.assetDescription || ''}`.trim()
                  : row.area
                    ? row.area.name
                    : ''}
              </td>
              {planthireEnabled && (
                <>
                  <td>{row?.customer?.name || ''}</td>
                  <td>{row?.project?.name || ''}</td>
                </>
              )}
              <td>{row.department?.name || ''}</td>
              <td>{row.workOrder?.workOrderNumber || ''}</td>
              <td>{row.form?.formType?.name || ''}</td>
              {isAssignedView ? (
                <>
                  <td>{row.assignedToName || ''}</td>
                  <td>{formattedTimestamp}</td>
                </>
              ) : (
                <>
                  <td>{enteredBy}</td>
                  <td>{formattedTimestamp}</td>
                  <td style={{ width: '130px' }}>
                    <div className="progress h-12-px">
                      <div
                        className="progress-bar bg-success rounded-pill"
                        role="progressbar"
                        style={{
                          width: `${Math.round(
                            (
                              row?.completedItemsCount == null || row?.requiredItemsCount == null
                                ? 0
                                : row.requiredItemsCount === 0
                                  ? 1
                                  : Math.min(row.completedItemsCount / row.requiredItemsCount, 1)
                            ) * 100
                          )}%`
                        }}
                        aria-valuenow={Math.round(
                          (
                            row?.completedItemsCount == null || row?.requiredItemsCount == null
                              ? 0
                              : row.requiredItemsCount === 0
                                ? 1
                                : Math.min(row.completedItemsCount / row.requiredItemsCount, 1)
                          ) * 100
                        )}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                      </div>
                    </div>
                  </td>
                  {isCompletedView ? (
                    <td>
                      {row.pdfLink ? (
                        <a
                          href={row.pdfLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-danger"
                          title="Open PDF"
                        >
                          <Icon icon="mdi:file-pdf-box" width="36" height="36" />
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  ) : (
                    showActions && (
                      <td className="text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            type="button"
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => onDelete && onDelete(row)}
                          >
                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                          </button>
                        </div>
                      </td>
                    )
                  )}
                </>
              )}
            </tr>
          );
        })}
        {loading && (
          <tr>
            <td colSpan={columnCount} className="text-center py-2">
              <span className="spinner-border spinner-border-sm" />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default FormHistoryDataTable;
